import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Package, ShoppingBag, Calendar, TrendingUp, Plus, 
  Edit, Trash2, Search, Filter, MoreVertical, Eye,
  DollarSign, Users, Clock, LogOut, ArrowUpDown, AlertTriangle,
  ChevronDown, BarChart3, ImageOff, Printer, Mail, Phone, MapPin, FileText, Hash,
  CheckCircle2, XCircle, Download, Check, X, Tag, Percent, Home, Film
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useAdminAuth } from '@/context/AdminAuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function AdminPage() {
  const navigate = useNavigate();
  const { logout, user } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('products');
  const [stats, setStats] = useState({
    products_count: 0,
    orders_count: 0,
    pending_orders: 0,
    appointments_count: 0,
    total_revenue: 0
  });
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [stockFilter, setStockFilter] = useState('all');
  const [onlyBroken, setOnlyBroken] = useState(false);
  const [brokenImages, setBrokenImages] = useState(() => new Set());
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderSearch, setOrderSearch] = useState('');
  const [orderSortBy, setOrderSortBy] = useState('recent');
  const [detailOrder, setDetailOrder] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [updatingOrder, setUpdatingOrder] = useState(false);
  const [discounts, setDiscounts] = useState([]);
  const [discountsLoading, setDiscountsLoading] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [brandList, setBrandList] = useState([]);
  const [discountForm, setDiscountForm] = useState({
    code: '', discount_type: 'percentage', value: '', scope: 'all', category: '', brand: 'all',
  });
  const [creatingDiscount, setCreatingDiscount] = useState(false);
  const HOME_FIELDS = [
    { key: 'hero_eyebrow', label: 'Hero — Sopratitolo', type: 'input', ph: "Gioielleria d'Arte dal 1968" },
    { key: 'hero_title_1', label: 'Hero — Titolo (riga 1)', type: 'input', ph: "L'Arte del" },
    { key: 'hero_title_2', label: 'Hero — Titolo (riga 2, corsivo)', type: 'input', ph: 'Gioiello' },
    { key: 'hero_subtitle', label: 'Hero — Sottotitolo', type: 'textarea', ph: "Ogni gioiello è un'opera d'arte unica..." },
    { key: 'story_paragraph_1', label: 'La Nostra Storia — Paragrafo 1', type: 'textarea', ph: 'Da tre generazioni...' },
    { key: 'story_paragraph_2', label: 'La Nostra Storia — Paragrafo 2', type: 'textarea', ph: "Ogni creazione è un'opera d'arte..." },
    { key: 'rainbow_eyebrow', label: 'Rainbow — Sopratitolo', type: 'input', ph: 'Nuova Collezione' },
    { key: 'rainbow_title', label: 'Rainbow — Titolo', type: 'input', ph: 'Collezione Rainbow' },
    { key: 'rainbow_subtitle', label: 'Rainbow — Descrizione', type: 'textarea', ph: 'Croci in oro impreziosite...' },
    { key: 'rainbow_cta', label: 'Rainbow — Testo link', type: 'input', ph: 'Scopri la Collezione' },
    { key: 'instagram_eyebrow', label: 'Instagram — Sopratitolo', type: 'input', ph: '@cirogallotti_jewerlydesign' },
    { key: 'instagram_title', label: 'Instagram — Titolo', type: 'input', ph: 'Seguici su Instagram' },
    { key: 'instagram_subtitle', label: 'Instagram — Descrizione', type: 'textarea', ph: 'Anteprime delle nuove creazioni...' },
    { key: 'cta_eyebrow', label: 'CTA finale — Sopratitolo', type: 'input', ph: 'Esperienza Esclusiva' },
    { key: 'cta_title', label: 'CTA finale — Titolo', type: 'input', ph: 'Vieni nel Nostro Showroom' },
    { key: 'cta_subtitle', label: 'CTA finale — Descrizione', type: 'textarea', ph: 'Prenota un appuntamento privato...' },
    { key: 'cta_button', label: 'CTA finale — Testo pulsante', type: 'input', ph: 'Prenota Ora' },
  ];
  const [homeCms, setHomeCms] = useState({});
  const [homeCmsLoading, setHomeCmsLoading] = useState(false);
  const [savingHome, setSavingHome] = useState(false);
  const HOME_IMAGE_FIELDS = [
    { key: 'hero_poster', label: 'Hero — Immagine di sfondo', def: '/hero-poster.jpg' },
    { key: 'story_poster', label: 'La Nostra Storia — Immagine', def: '/story-poster.jpg' },
    { key: 'cta_poster', label: 'CTA finale — Immagine', def: '/footer-poster.jpg' },
    { key: 'rainbow_1', label: 'Rainbow — Aurora Hawaii', def: '/rainbow-1.jpg' },
    { key: 'rainbow_2', label: 'Rainbow — Aurora Rio', def: '/rainbow-2.jpg' },
    { key: 'rainbow_3', label: 'Rainbow — Aurora Tokyo', def: '/rainbow-3.jpg' },
    { key: 'ig_1', label: 'Instagram 1', def: '/instagram-1.jpg' },
    { key: 'ig_2', label: 'Instagram 2', def: '/instagram-2.jpg' },
    { key: 'ig_3', label: 'Instagram 3', def: '/instagram-3.jpg' },
    { key: 'ig_4', label: 'Instagram 4', def: '/instagram-4.jpg' },
    { key: 'ig_5', label: 'Instagram 5', def: '/instagram-5.jpg' },
    { key: 'ig_6', label: 'Instagram 6', def: '/instagram-6.jpg' },
  ];
  const HOME_VIDEO_FIELDS = [
    { key: 'hero_video', label: 'Hero — Video di sfondo', def: '/hero-loop.mp4' },
    { key: 'story_video', label: 'La Nostra Storia — Video', def: '/story-loop.mp4' },
    { key: 'cta_video', label: 'CTA finale — Video', def: '/cta-loop.mp4' },
  ];
  const [uploadingImg, setUploadingImg] = useState(null);
  const [uploadingVid, setUploadingVid] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // Admin Users state
  const [adminUsers, setAdminUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '' });
  const [creatingUser, setCreatingUser] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') fetchAdminUsers();
    if (activeTab === 'appointments') fetchAppointments();
    if (activeTab === 'discounts') fetchDiscounts();
    if (activeTab === 'home') fetchHomeCms();
  }, [activeTab]);

  const fetchHomeCms = async () => {
    setHomeCmsLoading(true);
    try {
      const { data } = await axios.get(`${API}/site-content/home`);
      setHomeCms(data?.content || {});
    } catch (e) {
      toast.error('Errore nel caricamento contenuti Home');
    } finally {
      setHomeCmsLoading(false);
    }
  };

  const handleSaveHomeCms = async () => {
    setSavingHome(true);
    try {
      await axios.put(`${API}/admin/site-content/home`, { content: homeCms });
      toast.success('Contenuti Home salvati');
    } catch (e) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSavingHome(false);
    }
  };

  const handleHomeImageUpload = async (key, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Immagine troppo grande (max 15 MB)');
      return;
    }
    setUploadingImg(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/upload/image`, fd);
      const merged = { ...homeCms, [key]: data.url };
      setHomeCms(merged);
      await axios.put(`${API}/admin/site-content/home`, { content: merged });
      toast.success('Immagine aggiornata');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload immagine');
    } finally {
      setUploadingImg(null);
    }
  };

  const handleHomeImageReset = async (key) => {
    const merged = { ...homeCms };
    delete merged[key];
    setHomeCms(merged);
    try {
      await axios.put(`${API}/admin/site-content/home`, { content: merged });
      toast.success('Immagine ripristinata');
    } catch (e) {
      toast.error('Errore ripristino');
    }
  };

  const handleHomeVideoUpload = async (key, file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video troppo grande (max 50 MB). Comprimilo prima di caricarlo.');
      return;
    }
    setUploadingVid(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/upload/video`, fd);
      const merged = { ...homeCms, [key]: data.url };
      setHomeCms(merged);
      await axios.put(`${API}/admin/site-content/home`, { content: merged });
      toast.success('Video aggiornato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload video');
    } finally {
      setUploadingVid(null);
    }
  };

  const fetchDiscounts = async () => {
    setDiscountsLoading(true);
    try {
      const [dRes, cRes, bRes] = await Promise.all([
        axios.get(`${API}/admin/discounts`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/brands`),
      ]);
      setDiscounts(dRes.data);
      setCategoryList(cRes.data || []);
      setBrandList(bRes.data || []);
    } catch (e) {
      toast.error('Errore nel caricamento sconti');
    } finally {
      setDiscountsLoading(false);
    }
  };

  const handleCreateDiscount = async (e) => {
    e.preventDefault();
    if (!discountForm.code.trim() || !discountForm.value) {
      toast.error('Inserisci codice e valore');
      return;
    }
    if (discountForm.scope === 'category' && !discountForm.category) {
      toast.error('Seleziona una categoria');
      return;
    }
    setCreatingDiscount(true);
    try {
      const payload = {
        code: discountForm.code.trim().toUpperCase(),
        discount_type: discountForm.discount_type,
        value: parseFloat(discountForm.value),
        scope: discountForm.scope,
        category: discountForm.scope === 'category' ? discountForm.category : null,
        brand: discountForm.scope === 'category' && discountForm.brand !== 'all' ? discountForm.brand : null,
      };
      const { data } = await axios.post(`${API}/admin/discounts`, payload);
      setDiscounts((prev) => [data, ...prev]);
      setDiscountForm({ code: '', discount_type: 'percentage', value: '', scope: 'all', category: '', brand: 'all' });
      toast.success('Codice sconto creato');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore creazione codice');
    } finally {
      setCreatingDiscount(false);
    }
  };

  const handleToggleDiscount = async (d) => {
    try {
      await axios.put(`${API}/admin/discounts/${d.id}`, { active: !d.active });
      setDiscounts((prev) => prev.map((x) => (x.id === d.id ? { ...x, active: !d.active } : x)));
    } catch (e) {
      toast.error('Errore aggiornamento');
    }
  };

  const handleDeleteDiscount = async (id) => {
    if (!window.confirm('Eliminare questo codice sconto?')) return;
    try {
      await axios.delete(`${API}/admin/discounts/${id}`);
      setDiscounts((prev) => prev.filter((x) => x.id !== id));
      toast.success('Codice eliminato');
    } catch (e) {
      toast.error('Errore eliminazione');
    }
  };

  const fetchAppointments = async () => {
    setAppointmentsLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/appointments`);
      setAppointments(data);
    } catch (e) {
      toast.error('Errore nel caricamento appuntamenti');
    } finally {
      setAppointmentsLoading(false);
    }
  };

  const handleUpdateAppointmentStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/appointments/${id}/status`, null, { params: { status } });
      setAppointments((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success(status === 'confirmed' ? 'Appuntamento confermato' : 'Appuntamento annullato');
    } catch (e) {
      toast.error('Errore aggiornamento appuntamento');
    }
  };

  const handleDeleteAppointment = async (id) => {
    if (!window.confirm('Eliminare definitivamente questo appuntamento?')) return;
    try {
      await axios.delete(`${API}/admin/appointments/${id}`);
      setAppointments((prev) => prev.filter((a) => a.id !== id));
      toast.success('Appuntamento eliminato');
    } catch (e) {
      toast.error('Errore eliminazione');
    }
  };

  const handleUpdateOrderStatus = async (order, status, payment_status) => {
    setUpdatingOrder(true);
    try {
      const params = { status };
      if (payment_status) params.payment_status = payment_status;
      await axios.put(`${API}/admin/orders/${order.id}/status`, null, { params });
      const patch = { status, ...(payment_status ? { payment_status } : {}) };
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, ...patch } : o)));
      setDetailOrder((prev) => (prev && prev.id === order.id ? { ...prev, ...patch } : prev));
      toast.success(status === 'confirmed' ? 'Ordine confermato' : status === 'cancelled' ? 'Ordine annullato' : 'Ordine aggiornato');
    } catch (e) {
      toast.error('Errore aggiornamento ordine');
    } finally {
      setUpdatingOrder(false);
    }
  };

  const exportOrdersCsv = () => {
    const headers = ['Numero', 'Data', 'Cliente', 'Email', 'Telefono', 'Indirizzo', 'Citta', 'CAP', 'Stato', 'Pagamento', 'Totale'];
    const rows = filteredOrders.map((o) => [
      o.order_number != null ? `#${String(o.order_number).padStart(4, '0')}` : o.id,
      formatDate(o.created_at),
      o.customer_name, o.customer_email, o.customer_phone,
      o.shipping_address, o.shipping_city, o.shipping_postal,
      o.status, o.payment_status, (o.total || 0).toFixed(2),
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c == null ? '' : c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ordini_ciro_gallotti_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const orderLabel = (order) =>
    order.order_number != null ? `#${String(order.order_number).padStart(4, '0')}` : `#${(order.id || '').slice(0, 8)}`;

  const fetchAdminUsers = async () => {
    setUsersLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`);
      setAdminUsers(data);
    } catch (e) {
      toast.error('Errore nel caricamento utenti');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.username.trim() || newUser.password.length < 6) {
      toast.error('Username obbligatorio e password di almeno 6 caratteri');
      return;
    }
    setCreatingUser(true);
    try {
      await axios.post(`${API}/admin/users`, {
        username: newUser.username.trim(),
        password: newUser.password,
      });
      toast.success(`Utente "${newUser.username}" creato`);
      setNewUser({ username: '', password: '' });
      fetchAdminUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Eliminare definitivamente l'utente "${username}"?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${encodeURIComponent(username)}`);
      toast.success('Utente eliminato');
      fetchAdminUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleChangePassword = async (username) => {
    const newPass = window.prompt(`Nuova password per "${username}" (min. 6 caratteri):`);
    if (!newPass) return;
    if (newPass.length < 6) {
      toast.error('Password troppo corta');
      return;
    }
    try {
      await axios.put(`${API}/admin/users/${encodeURIComponent(username)}/password`, {
        password: newPass,
      });
      toast.success('Password aggiornata');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore aggiornamento');
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, productsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/orders`)
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
    } catch (e) {
      console.error('Error fetching admin data:', e);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;
    
    try {
      await axios.delete(`${API}/products/${productToDelete.id}`);
      toast.success('Prodotto eliminato');
      setProducts(products.filter(p => p.id !== productToDelete.id));
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    } catch (e) {
      toast.error('Errore eliminazione prodotto');
    }
  };

  const handleToggleField = async (product, field) => {
    const newValue = !product[field];
    try {
      await axios.put(`${API}/products/${product.id}`, { [field]: newValue });
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, [field]: newValue } : p)));
      if (field === 'in_stock') {
        toast.success(newValue ? 'Segnato come disponibile' : 'Segnato come esaurito');
      } else {
        toast.success(newValue ? 'Messo in evidenza' : 'Rimosso dall\'evidenza');
      }
    } catch (e) {
      toast.error('Errore aggiornamento prodotto');
    }
  };

  const markBroken = (productId) => {
    setBrokenImages((prev) => {
      if (prev.has(productId)) return prev;
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
  };

  const filteredProducts = products
    .filter((product) => {
      const matchesSearch =
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (product.model_code || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (product.description || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
      const matchesStock =
        stockFilter === 'all' ||
        (stockFilter === 'in' && product.in_stock) ||
        (stockFilter === 'out' && !product.in_stock);
      const matchesBroken = !onlyBroken || brokenImages.has(product.id) || !product.image_url;
      return matchesSearch && matchesCategory && matchesStock && matchesBroken;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-asc': return a.price - b.price;
        case 'price-desc': return b.price - a.price;
        case 'name': return a.name.localeCompare(b.name);
        case 'recent':
        default:
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
    });

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Mappa prodotti per arricchire gli articoli dell'ordine (foto + codice)
  const productsById = products.reduce((acc, p) => { acc[p.id] = p; return acc; }, {});
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const resolveImg = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `${origin}${url}`;
  };
  const getItemMeta = (item) => {
    const p = productsById[item.product_id] || {};
    return {
      name: item.product_name || item.name || p.name || 'Prodotto',
      model_code: p.model_code || item.model_code || '',
      image: resolveImg(p.image_url || item.image_url || ''),
      category: p.category || '',
    };
  };

  const buildInvoiceHtml = (order) => {
    const escapeHtml = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const rows = (order.items || []).map((item) => {
      const m = getItemMeta(item);
      const qty = item.quantity || 1;
      const unit = item.price || 0;
      const line = (item.item_total != null) ? item.item_total : unit * qty;
      return `
        <tr>
          <td class="cell-img">
            ${m.image ? `<img src="${m.image}" alt="" />` : '<div class="noimg">—</div>'}
          </td>
          <td>
            <div class="pname">${escapeHtml(m.name)}</div>
            ${m.category ? `<div class="pmeta">${escapeHtml(m.category)}</div>` : ''}
            ${item.size ? `<div class="pmeta">Misura: ${escapeHtml(item.size)}</div>` : ''}
          </td>
          <td class="pcode">${m.model_code ? escapeHtml(m.model_code) : '<span class="muted">—</span>'}</td>
          <td class="center">${qty}</td>
          <td class="right">${formatPrice(unit)}</td>
          <td class="right strong">${formatPrice(line)}</td>
        </tr>`;
    }).join('');

    const shipping = [order.shipping_address, [order.shipping_postal, order.shipping_city].filter(Boolean).join(' ')]
      .filter(Boolean).join(', ');

    return `<!DOCTYPE html><html lang="it"><head><meta charset="utf-8" />
      <title>Ordine ${escapeHtml(order.id)}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: 'Helvetica Neue', Arial, sans-serif; color:#1a1a1a; margin:0; padding:40px; }
        .head { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #722F37; padding-bottom:24px; margin-bottom:28px; }
        .brand img { height:56px; }
        .brand .tag { color:#722F37; font-size:11px; letter-spacing:2px; text-transform:uppercase; margin-top:8px; }
        .company { text-align:right; font-size:12px; color:#555; line-height:1.6; }
        .company .name { color:#1a1a1a; font-weight:600; font-size:13px; }
        .doc-title { font-size:26px; font-weight:700; margin:0 0 4px; letter-spacing:-0.5px; }
        .doc-sub { color:#777; font-size:12px; margin-bottom:28px; }
        .grid { display:flex; gap:40px; margin-bottom:32px; }
        .grid .col { flex:1; }
        .col h4 { font-size:10px; letter-spacing:1.5px; text-transform:uppercase; color:#722F37; margin:0 0 8px; }
        .col p { margin:2px 0; font-size:13px; color:#333; line-height:1.5; }
        table { width:100%; border-collapse:collapse; margin-top:8px; }
        th { text-align:left; font-size:10px; letter-spacing:1px; text-transform:uppercase; color:#999; border-bottom:1px solid #e5e5e5; padding:10px 8px; }
        td { padding:12px 8px; border-bottom:1px solid #f0f0f0; font-size:13px; vertical-align:middle; }
        .cell-img { width:64px; }
        .cell-img img { width:56px; height:56px; object-fit:cover; border-radius:6px; background:#f4f4f4; }
        .noimg { width:56px; height:56px; border-radius:6px; background:#f4f4f4; display:flex; align-items:center; justify-content:center; color:#bbb; }
        .pname { font-weight:600; }
        .pmeta { color:#999; font-size:11px; margin-top:2px; }
        .pcode { font-family:'SF Mono', Menlo, monospace; font-size:12px; color:#444; }
        .muted { color:#ccc; }
        .center { text-align:center; }
        .right { text-align:right; }
        .strong { font-weight:700; }
        .totals { margin-top:24px; margin-left:auto; width:280px; }
        .totals .row { display:flex; justify-content:space-between; padding:8px 0; font-size:13px; }
        .totals .grand { border-top:2px solid #1a1a1a; margin-top:6px; padding-top:12px; font-size:18px; font-weight:700; }
        .status-badges { margin-top:8px; }
        .badge { display:inline-block; font-size:11px; padding:4px 12px; border-radius:99px; margin-right:6px; text-transform:capitalize; }
        .footer { margin-top:48px; border-top:1px solid #e5e5e5; padding-top:16px; text-align:center; color:#999; font-size:11px; line-height:1.7; }
        @media print { body { padding:24px; } .no-print { display:none; } }
      </style></head><body>
      <div class="head">
        <div class="brand">
          <img src="${origin}/logo.png" alt="Ciro Gallotti" />
          <div class="tag">Gioielleria · Napoli</div>
        </div>
        <div class="company">
          <div class="name">Ciro Gallotti</div>
          Via Chiaia 138<br/>80121 Napoli, Italia<br/>
          Tel. +39 333 645 0082<br/>
          cirogallotti81@libero.it<br/>
          P.IVA 11025931210
        </div>
      </div>

      <h1 class="doc-title">Riepilogo Ordine</h1>
      <div class="doc-sub">Ordine ${escapeHtml(order.order_number != null ? '#' + String(order.order_number).padStart(4, '0') : order.id)} · ${escapeHtml(formatDate(order.created_at))}</div>

      <div class="grid">
        <div class="col">
          <h4>Cliente</h4>
          <p><strong>${escapeHtml(order.customer_name || '—')}</strong></p>
          <p>${escapeHtml(order.customer_email || '')}</p>
          ${order.customer_phone ? `<p>${escapeHtml(order.customer_phone)}</p>` : ''}
        </div>
        <div class="col">
          <h4>Spedizione</h4>
          <p>${escapeHtml(shipping || '—')}</p>
        </div>
        <div class="col">
          <h4>Stato</h4>
          <div class="status-badges">
            <span class="badge" style="background:#eef;color:#334">${escapeHtml(order.status || '—')}</span>
            <span class="badge" style="background:#efe;color:#363">${escapeHtml(order.payment_status || '—')}</span>
          </div>
        </div>
      </div>

      <table>
        <thead><tr>
          <th></th><th>Articolo</th><th>Codice</th><th class="center">Qtà</th><th class="right">Prezzo</th><th class="right">Totale</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>

      <div class="totals">
        <div class="row grand"><span>Totale</span><span>${formatPrice(order.total || 0)}</span></div>
      </div>

      <div class="footer">
        Grazie per aver scelto Ciro Gallotti — Alta Gioielleria dal 1968<br/>
        Documento generato il ${escapeHtml(formatDate(new Date().toISOString()))}
      </div>
    </body></html>`;
  };

  const handlePrintOrder = (order) => {
    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Abilita i popup per stampare il PDF');
      return;
    }
    w.document.open();
    w.document.write(buildInvoiceHtml(order));
    w.document.close();
    // Attendi il caricamento delle immagini prima di stampare
    const doPrint = () => { w.focus(); w.print(); };
    if (w.document.readyState === 'complete') {
      setTimeout(doPrint, 600);
    } else {
      w.onload = () => setTimeout(doPrint, 600);
    }
  };


  const categories = [...new Set(products.map(p => p.category))];

  // Panoramica (calcolata client-side, nessuna chiamata extra)
  const categoryCounts = categories
    .map((cat) => ({ cat, count: products.filter((p) => p.category === cat).length }))
    .sort((a, b) => b.count - a.count);
  const maxCatCount = Math.max(1, ...categoryCounts.map((c) => c.count));
  const outOfStockCount = products.filter((p) => !p.in_stock).length;
  const brokenCount = brokenImages.size;

  const orderStatuses = ['all', 'pending', 'confirmed', 'paid', 'cancelled'];
  const filteredOrders = orders.filter((o) => {
    const matchesStatus =
      orderStatusFilter === 'all' ||
      o.status === orderStatusFilter ||
      o.payment_status === orderStatusFilter;
    const q = orderSearch.toLowerCase();
    const matchesSearch =
      !q ||
      (o.customer_name || '').toLowerCase().includes(q) ||
      (o.customer_email || '').toLowerCase().includes(q);
    return matchesStatus && matchesSearch;
  }).sort((a, b) => {
    switch (orderSortBy) {
      case 'price-asc': return (a.total || 0) - (b.total || 0);
      case 'price-desc': return (b.total || 0) - (a.total || 0);
      case 'recent':
      default:
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    }
  });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="font-heading text-base md:text-lg text-gray-900">
                Ciro Gallotti
              </Link>
              <Badge variant="outline" className="rounded-full">Admin</Badge>
            </div>
            <div className="flex items-center gap-3">
              {user && (
                <span className="hidden sm:inline text-sm text-gray-500" data-testid="admin-username">
                  {user.username}
                </span>
              )}
              <Link to="/">
                <Button variant="outline" className="rounded-full" data-testid="back-to-site">
                  Torna al Sito
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full text-gray-500 hover:text-red-600"
                onClick={async () => { await logout(); navigate('/admin/login', { replace: true }); }}
                data-testid="admin-logout-btn"
                aria-label="Esci"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Prodotti</p>
                <p className="text-2xl font-semibold">{stats.products_count}</p>
              </div>
              <div className="w-12 h-12 bg-[#722F37]/10 rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6 text-[#722F37]" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Ordini</p>
                <p className="text-2xl font-semibold">{stats.orders_count}</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">In Attesa</p>
                <p className="text-2xl font-semibold">{stats.pending_orders}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Ricavi</p>
                <p className="text-2xl font-semibold">{formatPrice(stats.total_revenue)}</p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Panoramica */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8" data-testid="admin-panorama">
          <div className="bg-white rounded-2xl p-6 border border-gray-100 lg:col-span-2">
            <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#722F37]" />
              Prodotti per Categoria
            </h3>
            <div className="space-y-3">
              {categoryCounts.map(({ cat, count }) => (
                <div key={cat} className="flex items-center gap-3">
                  <span className="w-24 text-xs text-gray-500 truncate">{cat}</span>
                  <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#722F37] rounded-full transition-all duration-500"
                      style={{ width: `${(count / maxCatCount) * 100}%` }}
                    />
                  </div>
                  <span className="w-8 text-right text-xs font-medium text-gray-700 tabular-nums">{count}</span>
                </div>
              ))}
              {categoryCounts.length === 0 && (
                <p className="text-sm text-gray-400">Nessun prodotto</p>
              )}
            </div>
          </div>

          <div className="grid grid-rows-2 gap-4">
            <div className="bg-white rounded-2xl p-5 border border-gray-100 flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 mb-1">Esauriti</p>
                <p className="text-2xl font-semibold text-gray-900">{outOfStockCount}</p>
              </div>
              <div className="w-11 h-11 bg-red-50 rounded-xl flex items-center justify-center">
                <Package className="w-5 h-5 text-red-500" />
              </div>
            </div>
            <button
              type="button"
              onClick={() => { setActiveTab('products'); setOnlyBroken(true); }}
              className={`bg-white rounded-2xl p-5 border text-left transition-colors flex items-center justify-between ${
                brokenCount > 0 ? 'border-amber-300 hover:bg-amber-50' : 'border-gray-100'
              }`}
              data-testid="broken-images-card"
            >
              <div>
                <p className="text-xs text-gray-500 mb-1">Immagini mancanti</p>
                <p className="text-2xl font-semibold text-gray-900">{brokenCount}</p>
                {brokenCount > 0 && (
                  <p className="text-[11px] text-amber-600 mt-1">Clicca per filtrare e ricaricare</p>
                )}
              </div>
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${brokenCount > 0 ? 'bg-amber-100' : 'bg-gray-50'}`}>
                <ImageOff className={`w-5 h-5 ${brokenCount > 0 ? 'text-amber-600' : 'text-gray-400'}`} />
              </div>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="border-b border-gray-100">
            <nav className="flex">
              <button
                onClick={() => setActiveTab('products')}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'products' 
                    ? 'text-[#722F37] border-b-2 border-[#722F37]' 
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                data-testid="tab-products"
              >
                <Package className="w-4 h-4 inline mr-2" />
                Prodotti
              </button>
              <button
                onClick={() => setActiveTab('orders')}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'orders' 
                    ? 'text-[#722F37] border-b-2 border-[#722F37]' 
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                data-testid="tab-orders"
              >
                <ShoppingBag className="w-4 h-4 inline mr-2" />
                Ordini
              </button>
              <button
                onClick={() => setActiveTab('appointments')}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'appointments'
                    ? 'text-[#722F37] border-b-2 border-[#722F37]'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                data-testid="tab-appointments"
              >
                <Calendar className="w-4 h-4 inline mr-2" />
                Appuntamenti
              </button>
              <button
                onClick={() => setActiveTab('discounts')}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'discounts'
                    ? 'text-[#722F37] border-b-2 border-[#722F37]'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                data-testid="tab-discounts"
              >
                <Tag className="w-4 h-4 inline mr-2" />
                Sconti
              </button>
              <button
                onClick={() => setActiveTab('home')}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'home'
                    ? 'text-[#722F37] border-b-2 border-[#722F37]'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                data-testid="tab-home"
              >
                <Home className="w-4 h-4 inline mr-2" />
                Home
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'users'
                    ? 'text-[#722F37] border-b-2 border-[#722F37]'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                data-testid="tab-users"
              >
                <Users className="w-4 h-4 inline mr-2" />
                Utenti
              </button>
            </nav>
          </div>

          {/* Products Tab */}
          {activeTab === 'products' && (
            <div className="p-6">
              {/* Toolbar */}
              <div className="flex flex-col gap-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Cerca prodotti..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 rounded-full"
                      data-testid="search-products"
                    />
                  </div>
                  <Button 
                    onClick={() => navigate('/admin/prodotto/nuovo')}
                    className="bg-[#722F37] hover:bg-[#5A252C] rounded-full"
                    data-testid="add-product-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Prodotto
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-[170px] rounded-full" data-testid="filter-category">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte le categorie</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={stockFilter} onValueChange={setStockFilter}>
                    <SelectTrigger className="w-[150px] rounded-full" data-testid="filter-stock">
                      <Package className="w-4 h-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti gli stati</SelectItem>
                      <SelectItem value="in">Disponibili</SelectItem>
                      <SelectItem value="out">Esauriti</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-[170px] rounded-full" data-testid="sort-products">
                      <ArrowUpDown className="w-4 h-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recent">Più recenti</SelectItem>
                      <SelectItem value="price-asc">Prezzo crescente</SelectItem>
                      <SelectItem value="price-desc">Prezzo decrescente</SelectItem>
                      <SelectItem value="name">Nome A–Z</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant={onlyBroken ? 'default' : 'outline'}
                    onClick={() => setOnlyBroken((v) => !v)}
                    className={`rounded-full ${onlyBroken ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
                    data-testid="toggle-broken-filter"
                  >
                    <ImageOff className="w-4 h-4 mr-2" />
                    Immagini mancanti
                  </Button>
                  <span className="ml-auto text-sm text-gray-500" data-testid="products-count">
                    {filteredProducts.length} prodotti
                  </span>
                </div>
              </div>

              {/* Products Table */}
              {loading ? (
                <div className="space-y-4">
                  {[1,2,3].map(i => (
                    <div key={i} className="animate-pulse flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-1/3" />
                        <div className="h-3 bg-gray-200 rounded w-1/4" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Nessun prodotto trovato</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredProducts.map((product) => (
                    <div 
                      key={product.id} 
                      onClick={() => navigate(`/admin/prodotto/${product.id}`)}
                      className="flex items-center gap-4 p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer"
                      data-testid={`product-row-${product.id}`}
                    >
                      <div className="relative flex-shrink-0">
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded-lg bg-gray-100"
                          onError={() => markBroken(product.id)}
                        />
                        {(brokenImages.has(product.id) || !product.image_url) && (
                          <span
                            className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center"
                            title="Immagine mancante — ricarica la foto"
                            data-testid={`broken-badge-${product.id}`}
                          >
                            <AlertTriangle className="w-3.5 h-3.5" />
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">{product.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="rounded-full text-xs">
                            {product.category}
                          </Badge>
                          {product.model_code ? (
                            <span className="text-sm text-gray-500">Modello - {product.model_code}</span>
                          ) : (
                            <span className="text-sm text-amber-600 font-medium">Modello</span>
                          )}
                          {(brokenImages.has(product.id) || !product.image_url) && (
                            <span className="text-xs text-amber-600 font-medium">Immagine mancante</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">{product.price_on_request ? 'Su richiesta' : formatPrice(product.price)}</p>
                        <p className={`text-xs ${product.in_stock ? 'text-green-600' : 'text-red-600'}`}>
                          {product.in_stock ? 'Disponibile' : 'Esaurito'}
                        </p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="rounded-full" onClick={(e) => e.stopPropagation()}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/prodotto/${product.id}`); }}>
                            <Eye className="w-4 h-4 mr-2" />
                            Visualizza
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/admin/prodotto/${product.id}`); }}>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifica
                          </DropdownMenuItem>

                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs text-gray-400 font-normal">Modifiche rapide</DropdownMenuLabel>
                          <div
                            className="px-2 py-1.5 flex items-center justify-between gap-4"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="text-sm text-gray-700">Disponibile</span>
                            <Switch
                              checked={!!product.in_stock}
                              onCheckedChange={() => handleToggleField(product, 'in_stock')}
                              data-testid={`toggle-stock-${product.id}`}
                            />
                          </div>
                          <div
                            className="px-2 py-1.5 flex items-center justify-between gap-4"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="text-sm text-gray-700">In evidenza</span>
                            <Switch
                              checked={!!product.featured}
                              onCheckedChange={() => handleToggleField(product, 'featured')}
                              data-testid={`toggle-featured-${product.id}`}
                            />
                          </div>
                          <DropdownMenuSeparator />

                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              setProductToDelete(product);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="p-6">
              {/* Order toolbar */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Cerca per cliente o email..."
                    value={orderSearch}
                    onChange={(e) => setOrderSearch(e.target.value)}
                    className="pl-10 rounded-full"
                    data-testid="search-orders"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={orderSortBy} onValueChange={setOrderSortBy}>
                    <SelectTrigger className="w-[180px] rounded-full" data-testid="sort-orders">
                      <ArrowUpDown className="w-4 h-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recent">Più recenti</SelectItem>
                      <SelectItem value="price-asc">Prezzo crescente</SelectItem>
                      <SelectItem value="price-desc">Prezzo decrescente</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={exportOrdersCsv}
                    className="rounded-full gap-1.5"
                    data-testid="export-orders-csv"
                  >
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">CSV</span>
                  </Button>
                  {orderStatuses.map((s) => (
                    <button
                      key={s}
                      onClick={() => setOrderStatusFilter(s)}
                      className={`px-4 py-2 text-xs font-medium rounded-full transition-colors capitalize ${
                        orderStatusFilter === s
                          ? 'bg-[#722F37] text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid={`order-filter-${s}`}
                    >
                      {s === 'all' ? 'Tutti' : s}
                    </button>
                  ))}
                </div>
              </div>

              {filteredOrders.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Nessun ordine</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredOrders.map((order) => (
                    <div
                      key={order.id}
                      onClick={() => setDetailOrder(order)}
                      className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 hover:shadow-sm transition-all cursor-pointer border border-transparent hover:border-gray-200"
                      data-testid={`order-row-${order.id}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="min-w-0">
                          <p className="text-xs font-mono text-[#722F37] font-semibold mb-0.5">{orderLabel(order)}</p>
                          <p className="font-medium text-gray-900 truncate">{order.customer_name}</p>
                          <p className="text-sm text-gray-500 truncate">{order.customer_email}</p>
                        </div>
                        <div className="text-right flex items-center gap-3 flex-shrink-0">
                          <div>
                            <p className="font-semibold text-gray-900">{formatPrice(order.total)}</p>
                            <p className="text-xs text-gray-500">{formatDate(order.created_at)}</p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handlePrintOrder(order); }}
                            className="rounded-full h-9 px-3 gap-1.5"
                            data-testid={`order-print-${order.id}`}
                          >
                            <Printer className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline text-xs">PDF</span>
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`rounded-full ${getStatusColor(order.status)}`}>{order.status}</Badge>
                        <Badge className={`rounded-full ${getStatusColor(order.payment_status)}`}>{order.payment_status}</Badge>
                        <div className="ml-auto flex items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleUpdateOrderStatus(order, 'confirmed', 'paid'); }}
                            disabled={updatingOrder || order.status === 'confirmed'}
                            className="rounded-full h-8 px-3 bg-green-600 hover:bg-green-700 text-white gap-1 disabled:opacity-40"
                            data-testid={`order-row-confirm-${order.id}`}
                          >
                            <Check className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline text-xs">Conferma</span>
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleUpdateOrderStatus(order, 'cancelled'); }}
                            disabled={updatingOrder || order.status === 'cancelled'}
                            className="rounded-full h-8 px-3 border-red-200 text-red-600 hover:bg-red-50 gap-1 disabled:opacity-40"
                            data-testid={`order-row-cancel-${order.id}`}
                          >
                            <X className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline text-xs">Annulla</span>
                          </Button>
                          <span className="text-sm text-gray-400 flex items-center gap-1 pl-1">
                            {order.items?.length || 0} art.
                            <Eye className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Appointments Tab */}
          {activeTab === 'appointments' && (
            <div className="p-6" data-testid="appointments-panel">
              {appointmentsLoading ? (
                <div className="text-center py-12 text-gray-400 text-sm">Caricamento…</div>
              ) : appointments.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Nessun appuntamento</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {appointments.map((appt) => (
                    <div
                      key={appt.id}
                      className="bg-gray-50 rounded-xl p-4 border border-transparent hover:border-gray-200 transition-colors"
                      data-testid={`appointment-row-${appt.id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-gray-900">{appt.name}</p>
                            <Badge className={`rounded-full ${getStatusColor(appt.status)}`}>{appt.status}</Badge>
                          </div>
                          <p className="text-sm text-gray-500 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{appt.email}</p>
                          <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5"><Phone className="w-3.5 h-3.5" />{appt.phone}</p>
                          {appt.message && (
                            <p className="text-sm text-gray-600 mt-2 bg-white rounded-lg p-2 border border-gray-100">{appt.message}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-start sm:items-end gap-2 flex-shrink-0">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-900 bg-white rounded-full px-3 py-1.5 border border-gray-100">
                            <Calendar className="w-3.5 h-3.5 text-[#722F37]" />
                            {appt.date} · {appt.time}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => handleUpdateAppointmentStatus(appt.id, 'confirmed')}
                              disabled={appt.status === 'confirmed'}
                              className="rounded-full h-8 bg-green-600 hover:bg-green-700 text-white gap-1 disabled:opacity-40"
                              data-testid={`appointment-confirm-${appt.id}`}
                            >
                              <Check className="w-3.5 h-3.5" /> Conferma
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateAppointmentStatus(appt.id, 'cancelled')}
                              disabled={appt.status === 'cancelled'}
                              className="rounded-full h-8 border-red-200 text-red-600 hover:bg-red-50 gap-1 disabled:opacity-40"
                              data-testid={`appointment-cancel-${appt.id}`}
                            >
                              <X className="w-3.5 h-3.5" /> Annulla
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={() => handleDeleteAppointment(appt.id)}
                              className="rounded-full h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50"
                              data-testid={`appointment-delete-${appt.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}


          {/* Discounts Tab */}
          {activeTab === 'discounts' && (
            <div className="p-6" data-testid="discounts-panel">
              {/* Create form */}
              <form onSubmit={handleCreateDiscount} className="bg-gray-50 rounded-2xl p-5 mb-6 border border-gray-100" data-testid="discount-form">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-[#722F37]" /> Nuovo codice sconto
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Codice</label>
                    <Input
                      value={discountForm.code}
                      onChange={(e) => setDiscountForm({ ...discountForm, code: e.target.value.toUpperCase() })}
                      placeholder="ES. ESTATE15"
                      className="rounded-full bg-white uppercase"
                      data-testid="discount-code-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Tipo</label>
                    <Select value={discountForm.discount_type} onValueChange={(v) => setDiscountForm({ ...discountForm, discount_type: v })}>
                      <SelectTrigger className="rounded-full bg-white" data-testid="discount-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentuale (%)</SelectItem>
                        <SelectItem value="fixed">Importo fisso (€)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      Valore {discountForm.discount_type === 'percentage' ? '(%)' : '(€)'}
                    </label>
                    <Input
                      type="number" min="0" step="0.01"
                      value={discountForm.value}
                      onChange={(e) => setDiscountForm({ ...discountForm, value: e.target.value })}
                      placeholder={discountForm.discount_type === 'percentage' ? '15' : '50'}
                      className="rounded-full bg-white"
                      data-testid="discount-value-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Ambito</label>
                    <Select value={discountForm.scope} onValueChange={(v) => setDiscountForm({ ...discountForm, scope: v })}>
                      <SelectTrigger className="rounded-full bg-white" data-testid="discount-scope-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutto lo store</SelectItem>
                        <SelectItem value="category">Categoria specifica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {discountForm.scope === 'category' && (
                    <>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Categoria</label>
                        <Select value={discountForm.category} onValueChange={(v) => setDiscountForm({ ...discountForm, category: v })}>
                          <SelectTrigger className="rounded-full bg-white" data-testid="discount-category-select"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                          <SelectContent>
                            {categoryList.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Marca (opzionale)</label>
                        <Select value={discountForm.brand} onValueChange={(v) => setDiscountForm({ ...discountForm, brand: v })}>
                          <SelectTrigger className="rounded-full bg-white" data-testid="discount-brand-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Tutte le marche</SelectItem>
                            {brandList.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                </div>
                <div className="mt-4">
                  <Button type="submit" disabled={creatingDiscount} className="rounded-full bg-[#722F37] hover:bg-[#5a252b] text-white gap-2" data-testid="discount-create-btn">
                    <Plus className="w-4 h-4" /> Crea codice
                  </Button>
                </div>
              </form>

              {/* List */}
              {discountsLoading ? (
                <div className="text-center py-12 text-gray-400 text-sm">Caricamento…</div>
              ) : discounts.length === 0 ? (
                <div className="text-center py-12">
                  <Tag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Nessun codice sconto</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {discounts.map((d) => (
                    <div key={d.id} className="bg-white border border-gray-100 rounded-xl p-4 flex items-center justify-between gap-4" data-testid={`discount-row-${d.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${d.active ? 'bg-[#722F37]/10 text-[#722F37]' : 'bg-gray-100 text-gray-400'}`}>
                          {d.discount_type === 'percentage' ? <Percent className="w-5 h-5" /> : <Tag className="w-5 h-5" />}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-gray-900 tracking-wide">{d.code}</span>
                            <Badge className={`rounded-full ${d.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                              {d.active ? 'Attivo' : 'Disattivo'}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5">
                            <span className="font-medium text-gray-700">
                              {d.discount_type === 'percentage' ? `${d.value}%` : formatPrice(d.value)}
                            </span>
                            {' · '}
                            {d.scope === 'all' ? 'Tutto lo store' : `Categoria: ${d.category}${d.brand ? ` (${d.brand})` : ''}`}
                            {d.usage_count > 0 && <span className="text-gray-400"> · usato {d.usage_count}×</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          type="button" size="sm" variant="outline"
                          onClick={() => handleToggleDiscount(d)}
                          className="rounded-full h-8 text-xs"
                          data-testid={`discount-toggle-${d.id}`}
                        >
                          {d.active ? 'Disattiva' : 'Attiva'}
                        </Button>
                        <Button
                          type="button" size="icon" variant="ghost"
                          onClick={() => handleDeleteDiscount(d.id)}
                          className="rounded-full h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50"
                          data-testid={`discount-delete-${d.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}


          {/* Home CMS Tab */}
          {activeTab === 'home' && (
            <div className="p-6" data-testid="home-cms-panel">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <Home className="w-4 h-4 text-[#722F37]" /> Contenuti Homepage
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">Modifica i testi principali della home. Lascia vuoto per usare il testo predefinito.</p>
                </div>
                <Button
                  type="button"
                  onClick={handleSaveHomeCms}
                  disabled={savingHome}
                  className="rounded-full bg-[#722F37] hover:bg-[#5a252b] text-white gap-2"
                  data-testid="home-cms-save-btn"
                >
                  <Check className="w-4 h-4" /> {savingHome ? 'Salvataggio…' : 'Salva'}
                </Button>
              </div>

              {homeCmsLoading ? (
                <div className="text-center py-12 text-gray-400 text-sm">Caricamento…</div>
              ) : (
                <div className="space-y-5 max-w-2xl">
                  {HOME_FIELDS.map((f) => (
                    <div key={f.key}>
                      <label className="text-xs font-medium text-gray-600 mb-1.5 block">{f.label}</label>
                      {f.type === 'textarea' ? (
                        <textarea
                          value={homeCms[f.key] || ''}
                          onChange={(e) => setHomeCms({ ...homeCms, [f.key]: e.target.value })}
                          placeholder={f.ph}
                          rows={3}
                          className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#722F37]/30 focus:border-[#722F37] resize-y"
                          data-testid={`home-field-${f.key}`}
                        />
                      ) : (
                        <Input
                          value={homeCms[f.key] || ''}
                          onChange={(e) => setHomeCms({ ...homeCms, [f.key]: e.target.value })}
                          placeholder={f.ph}
                          className="rounded-xl bg-gray-50"
                          data-testid={`home-field-${f.key}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Immagini Home */}
              {!homeCmsLoading && (
                <div className="mt-10 pt-8 border-t border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-1">
                    <ImageOff className="w-4 h-4 text-[#722F37]" /> Immagini Homepage
                  </h3>
                  <p className="text-xs text-gray-500 mb-5">Carica per sostituire; il salvataggio è automatico. "Ripristina" riporta l'immagine predefinita.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {HOME_IMAGE_FIELDS.map((f) => {
                      const current = homeCms[f.key] || f.def;
                      const isCustom = !!homeCms[f.key];
                      return (
                        <div key={f.key} className="group" data-testid={`home-image-${f.key}`}>
                          <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                            <img src={current} alt={f.label} className="w-full h-full object-cover" />
                            {uploadingImg === f.key && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              </div>
                            )}
                            <label className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors cursor-pointer opacity-0 group-hover:opacity-100">
                              <span className="text-white text-xs font-medium bg-[#722F37] px-3 py-1.5 rounded-full flex items-center gap-1">
                                <Plus className="w-3.5 h-3.5" /> Cambia
                              </span>
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                className="hidden"
                                onChange={(e) => handleHomeImageUpload(f.key, e.target.files?.[0])}
                                data-testid={`home-image-input-${f.key}`}
                              />
                            </label>
                          </div>
                          <div className="flex items-center justify-between mt-1.5">
                            <p className="text-xs text-gray-600 truncate">{f.label}</p>
                            {isCustom && (
                              <button
                                type="button"
                                onClick={() => handleHomeImageReset(f.key)}
                                className="text-xs text-gray-400 hover:text-red-600 flex-shrink-0 ml-1"
                                data-testid={`home-image-reset-${f.key}`}
                              >
                                Ripristina
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Video Home */}
              {!homeCmsLoading && (
                <div className="mt-10 pt-8 border-t border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-1">
                    <Film className="w-4 h-4 text-[#722F37]" /> Video di sfondo
                  </h3>
                  <p className="text-xs text-gray-500 mb-5">Carica un video MP4 (max 50 MB, consigliato breve e compresso). Salvataggio automatico. "Ripristina" riporta il video predefinito.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {HOME_VIDEO_FIELDS.map((f) => {
                      const current = homeCms[f.key] || f.def;
                      const isCustom = !!homeCms[f.key];
                      return (
                        <div key={f.key} data-testid={`home-video-${f.key}`}>
                          <div className="relative aspect-video rounded-xl overflow-hidden bg-gray-900 border border-gray-200">
                            <video
                              src={current}
                              muted
                              loop
                              playsInline
                              autoPlay
                              className="w-full h-full object-cover"
                            />
                            {uploadingVid === f.key && (
                              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              </div>
                            )}
                            <label className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/50 transition-colors cursor-pointer">
                              <span className="text-white text-xs font-medium bg-[#722F37] px-3 py-1.5 rounded-full flex items-center gap-1">
                                <Plus className="w-3.5 h-3.5" /> Cambia video
                              </span>
                              <input
                                type="file"
                                accept="video/mp4,video/webm,video/quicktime,video/ogg"
                                className="hidden"
                                onChange={(e) => handleHomeVideoUpload(f.key, e.target.files?.[0])}
                                data-testid={`home-video-input-${f.key}`}
                              />
                            </label>
                          </div>
                          <div className="flex items-center justify-between mt-1.5">
                            <p className="text-xs text-gray-600 truncate">{f.label}</p>
                            {isCustom && (
                              <button
                                type="button"
                                onClick={() => handleHomeImageReset(f.key)}
                                className="text-xs text-gray-400 hover:text-red-600 flex-shrink-0 ml-1"
                                data-testid={`home-video-reset-${f.key}`}
                              >
                                Ripristina
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}


            </div>
          )}


          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="p-6" data-testid="users-panel">
              <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                <h3 className="font-medium text-gray-900 mb-1">Crea Nuovo Amministratore</h3>
                <p className="text-sm text-gray-500 mb-5">
                  Gli account creati qui sono salvati nel database e non richiedono redeploy.
                </p>
                <form onSubmit={handleCreateUser} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3">
                  <Input
                    placeholder="Username (es. MARIO)"
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    required
                    minLength={2}
                    maxLength={64}
                    className="rounded-xl"
                    data-testid="new-user-username"
                  />
                  <Input
                    type="password"
                    placeholder="Password (min. 6 caratteri)"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    required
                    minLength={6}
                    className="rounded-xl"
                    data-testid="new-user-password"
                  />
                  <Button
                    type="submit"
                    disabled={creatingUser}
                    className="rounded-full bg-[#722F37] hover:bg-[#5a252b] text-white"
                    data-testid="create-user-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {creatingUser ? 'Creazione…' : 'Crea'}
                  </Button>
                </form>
              </div>

              {usersLoading ? (
                <div className="text-center py-12 text-gray-400 text-sm">Caricamento…</div>
              ) : adminUsers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Nessun amministratore</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {adminUsers.map((u) => (
                    <div
                      key={u.username}
                      className="flex items-center justify-between gap-4 p-4 bg-gray-50 rounded-xl"
                      data-testid={`user-row-${u.username}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-[#722F37]/10 flex items-center justify-center text-[#722F37] font-medium text-sm flex-shrink-0">
                          {u.username.slice(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{u.username}</p>
                          <p className="text-xs text-gray-500">
                            {u.role || 'admin'}
                            {u.username === user?.username && ' · (tu)'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          onClick={() => handleChangePassword(u.username)}
                          data-testid={`change-pw-${u.username}`}
                        >
                          Cambia password
                        </Button>
                        {u.username !== user?.username && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="rounded-full text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteUser(u.username)}
                            data-testid={`delete-user-${u.username}`}
                            aria-label={`Elimina ${u.username}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo prodotto?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare "{productToDelete?.name}". Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteProduct}
              className="bg-red-600 hover:bg-red-700 rounded-full"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Order Detail Dialog */}
      <Dialog open={!!detailOrder} onOpenChange={(o) => { if (!o) setDetailOrder(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
          {detailOrder && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-4 pr-6">
                  <div>
                    <DialogTitle className="text-xl">Ordine {orderLabel(detailOrder)}</DialogTitle>
                    <p className="text-xs text-gray-400 mt-1 font-mono">{detailOrder.id}</p>
                  </div>
                  <Button
                    type="button"
                    onClick={() => handlePrintOrder(detailOrder)}
                    className="rounded-full bg-[#722F37] hover:bg-[#5a252b] text-white gap-2"
                    data-testid="order-detail-print-btn"
                  >
                    <Printer className="w-4 h-4" />
                    Stampa PDF
                  </Button>
                </div>
              </DialogHeader>

              <div className="flex items-center gap-2 mb-4">
                <Badge className={`rounded-full ${getStatusColor(detailOrder.status)}`}>{detailOrder.status}</Badge>
                <Badge className={`rounded-full ${getStatusColor(detailOrder.payment_status)}`}>{detailOrder.payment_status}</Badge>
                <span className="text-xs text-gray-400 ml-auto flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />{formatDate(detailOrder.created_at)}
                </span>
              </div>

              {/* Azioni stato ordine */}
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <Button
                  type="button"
                  onClick={() => handleUpdateOrderStatus(detailOrder, 'confirmed', 'paid')}
                  disabled={updatingOrder || detailOrder.status === 'confirmed'}
                  className="rounded-full bg-green-600 hover:bg-green-700 text-white gap-1.5 disabled:opacity-50"
                  data-testid="order-confirm-btn"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Conferma ordine
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleUpdateOrderStatus(detailOrder, 'cancelled')}
                  disabled={updatingOrder || detailOrder.status === 'cancelled'}
                  className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 gap-1.5 disabled:opacity-50"
                  data-testid="order-cancel-btn"
                >
                  <XCircle className="w-4 h-4" />
                  Annulla ordine
                </Button>
                {detailOrder.status !== 'pending' && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleUpdateOrderStatus(detailOrder, 'pending', 'pending')}
                    disabled={updatingOrder}
                    className="rounded-full text-gray-500 gap-1.5"
                    data-testid="order-reset-btn"
                  >
                    <Clock className="w-4 h-4" />
                    Rimetti in attesa
                  </Button>
                )}
              </div>

              {/* Cliente + Spedizione */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-[10px] tracking-widest uppercase text-[#722F37] font-semibold mb-2">Cliente</h4>
                  <p className="text-sm font-medium text-gray-900">{detailOrder.customer_name}</p>
                  <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-1"><Mail className="w-3.5 h-3.5" />{detailOrder.customer_email}</p>
                  {detailOrder.customer_phone && (
                    <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-1"><Phone className="w-3.5 h-3.5" />{detailOrder.customer_phone}</p>
                  )}
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-[10px] tracking-widest uppercase text-[#722F37] font-semibold mb-2">Spedizione</h4>
                  <p className="text-sm text-gray-700 flex items-start gap-1.5">
                    <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      {detailOrder.shipping_address || '—'}
                      {(detailOrder.shipping_postal || detailOrder.shipping_city) && (
                        <><br />{[detailOrder.shipping_postal, detailOrder.shipping_city].filter(Boolean).join(' ')}</>
                      )}
                    </span>
                  </p>
                </div>
              </div>

              {/* Articoli */}
              <h4 className="text-[10px] tracking-widest uppercase text-gray-400 font-semibold mb-2 flex items-center gap-1.5">
                <Package className="w-3.5 h-3.5" />Articoli ({detailOrder.items?.length || 0})
              </h4>
              <div className="space-y-2">
                {(detailOrder.items || []).map((item, i) => {
                  const m = getItemMeta(item);
                  const qty = item.quantity || 1;
                  const line = (item.item_total != null) ? item.item_total : (item.price || 0) * qty;
                  return (
                    <div key={i} className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl p-3" data-testid={`detail-item-${i}`}>
                      {m.image ? (
                        <img src={m.image} alt={m.name} className="w-14 h-14 rounded-lg object-cover bg-gray-100 flex-shrink-0" />
                      ) : (
                        <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><ImageOff className="w-5 h-5 text-gray-300" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{m.name}</p>
                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                          <Hash className="w-3 h-3" />{m.model_code || '—'}
                          {m.category && <span className="ml-2">· {m.category}</span>}
                          {item.size && <span className="ml-2 text-gray-700 font-medium" data-testid={`detail-item-size-${i}`}>· Misura {item.size}</span>}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-gray-900">{formatPrice(line)}</p>
                        <p className="text-xs text-gray-400">{qty} × {formatPrice(item.price || 0)}</p>
                      </div>
                    </div>
                  );
                })}
                {(!detailOrder.items || detailOrder.items.length === 0) && (
                  <p className="text-sm text-gray-400">Nessun dettaglio articoli</p>
                )}
              </div>

              {/* Totale */}
              <div className="flex items-center justify-between mt-5 pt-4 border-t-2 border-gray-900">
                <span className="text-sm font-medium text-gray-500">Totale ordine</span>
                <span className="text-2xl font-bold text-gray-900">{formatPrice(detailOrder.total || 0)}</span>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
